import { Role } from './types/user';

// generate cryotographically secure randon string of size between min and max
const generateString = (min: number, max: number) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let randomString = '';
  do {
    const array = new Uint8Array(Math.floor(Math.random() * max - min) + min + 1);
    crypto.getRandomValues(array);
    randomString += Array.from(array, (char: number) => chars[char % chars.length]).join('');
  } while (randomString.length < min);

  return randomString.slice(0, max);
};

export const generateQrToken = () => generateString(48, 64);

export const generateRandomPassword = () => generateString(8, 10);

export const createDebounce = (debounceTime: number) => {
  let debounceTimeoutId: ReturnType<typeof setTimeout> | null = null;
  const debounce = (func: () => unknown) => {
    if (debounceTimeoutId) clearTimeout(debounceTimeoutId);
    debounceTimeoutId = setTimeout(() => {
      func();
      debounceTimeoutId = null;
    }, debounceTime);
  };

  return debounce;
};

export const retryBooleanPromise = async (request: () => Promise<boolean>, numberOfReties = 3) => {
  for (let i = 0; i < numberOfReties; i++) {
    if (await request()) {
      return true;
    }
  }

  return false;
};

export const isEmployeeOrAdmin = (role: Role) => ['employee', 'admin'].includes(role);

/**
 * The function receives a promise and wrap it with another promise that has a capability of ignore resolving or errors.
 * It helps to avoid possible race conditions.
 */
export const abortablePromise = <T extends Promise<unknown>>(promise: T): { abort: () => void; promise: T } => {
  let aborted = false;
  const abort = () => {
    aborted = true;
  };
  const p = new Promise((resolve, reject) => {
    promise
      .then((e) => {
        if (!aborted) {
          resolve(e);
        }
      })
      .catch((e) => {
        if (!aborted) {
          reject(e);
        }
      });
  }) as T;

  return {
    promise: p,
    abort,
  };
};
